import { Outlet, useLocation } from 'react-router'
import { motion } from 'framer-motion'

import useHelmet from 'utilities/hooks/helmet/useHelmet'

/**
 * The shell for the قرعه‌کشی landing — full width, at every size.
 *
 * `FundLayout` caps a route at the app frame (`.app-frame`, 720px by
 * default). That is deliberate and right for the app: those routes are phone
 * screens, and a phone screen stretched across 1440px misrepresents the
 * product. It is wrong for a landing page, which is the one thing here that
 * is not a phone screen — and it was wrong invisibly: when the frame was 30%
 * of the window, a `max-w-[960px]` simply never applied and every layout fix
 * silently did nothing.
 *
 * So the split is by what the route *is*: `/q/<code>` (the pitch) gets this,
 * and `/q/<code>/demo` keeps `FundLayout`, because the demo IS a phone screen
 * and showing it any other way would be a lie about what the reader gets.
 */
const LandingLayout = ({ helmet }: { helmet: string }) => {
    const { pathname } = useLocation()
    useHelmet(helmet)

    return (
        <div className="h-full w-full bg-white">
            <motion.div
                key={pathname}
                initial={{ opacity: 0, x: '100%' }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: '-100%' }}
                transition={{ duration: 0.2 }}
                className="min-h-screen w-full"
            >
                <Outlet />
            </motion.div>
        </div>
    )
}

export default LandingLayout
